import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge, Button, Card } from '@/components/ui';
import { useAdminSentryHealth } from './hooks/useAdminSentryHealth';
import type { Status as SentryHealthStatus } from './hooks/useAdminSentryHealth';
import type { EdgeMonitoringHealth } from '@/types/sentry-health.types';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info';

type BreadcrumbRow = {
  name: string;
  stage: string;
  data: string;
  tooltip: string;
};
type WebFlow = {
  name: string;
  file: string;
  note: string;
  breadcrumbs: BreadcrumbRow[];
};
type WiredFn = {
  name: string;
  priority: 'critical' | 'high';
  note: string;
  breadcrumbs: BreadcrumbRow[];
};

const WIRED_FUNCTIONS: WiredFn[] = [
  {
    name: 'webhooks-gmail-watch',
    priority: 'critical',
    note: '14 checkpoints',
    breadcrumbs: [
      {
        name: 'pubsub.received',
        stage: 'Pub/Sub parse',
        data: 'email_address, notification_history_id',
        tooltip:
          'A new email notification arrived from Google. Reading which Gmail account it is for.',
      },
      {
        name: 'subscription.resolved',
        stage: 'DB lookup',
        data: 'subscription_id, household_id, has_stored_history_id',
        tooltip:
          'Looked up which household this Gmail account belongs to in the database.',
      },
      {
        name: 'oauth.token_refreshed',
        stage: 'Token refresh',
        data: '—',
        tooltip: "Got a fresh access token to read Gmail on the user's behalf.",
      },
      {
        name: 'oauth.token_refresh_failed',
        stage: 'Token refresh ❌',
        data: 'revoked (bool)',
        tooltip:
          'Could not renew Gmail access — the user may have revoked permission.',
      },
      {
        name: 'gmail.history_fetched',
        stage: 'Gmail API',
        data: 'message_count, start_history_id, latest_history_id',
        tooltip:
          'Retrieved the list of new emails from Gmail since the last check.',
      },
      {
        name: 'message.skipped_duplicate_id',
        stage: 'Dedup per msg',
        data: 'gmail_message_id',
        tooltip: 'This email was already saved — skipping to avoid duplicates.',
      },
      {
        name: 'message.skipped_duplicate_hash',
        stage: 'Dedup per msg',
        data: 'gmail_message_id',
        tooltip: "This email's content matches one already saved — skipping.",
      },
      {
        name: 'message.skipped_duplicate_constraint',
        stage: 'Dedup per msg',
        data: 'gmail_message_id',
        tooltip:
          'The database blocked saving this email because it already exists.',
      },
      {
        name: 'message.rejected_allowlist',
        stage: 'Allowlist per msg',
        data: 'gmail_message_id, sender_domain',
        tooltip:
          "Skipped an email from a sender not on the household's approved list.",
      },
      {
        name: 'db.email_insert_failed',
        stage: 'DB write per msg ❌',
        data: 'gmail_message_id, pg_code',
        tooltip: 'Failed to save this email to the database.',
      },
      {
        name: 'db.email_inserted',
        stage: 'DB write per msg',
        data: 'email_id, gmail_message_id',
        tooltip: 'Email saved successfully and added to the processing queue.',
      },
      {
        name: 'pgmq.send_failed',
        stage: 'PGMQ enqueue ❌',
        data: 'email_id, error',
        tooltip:
          'Email was saved but could not be added to the processing queue.',
      },
      {
        name: 'messages.batch_complete',
        stage: 'Batch summary',
        data: 'total, inserted, skipped_duplicate, rejected_allowlist',
        tooltip:
          'Finished processing all new emails in this notification batch.',
      },
      {
        name: 'db.history_id_updated',
        stage: 'History ID sync',
        data: 'from, to',
        tooltip:
          'Updated our bookmark so the next check only fetches newer emails.',
      },
    ],
  },
  {
    name: 'webhooks-stripe',
    priority: 'critical',
    note: '13 checkpoints across 3 event types',
    breadcrumbs: [
      {
        name: 'stripe.signature_verified',
        stage: 'Signature check',
        data: '—',
        tooltip:
          'Confirmed this webhook really came from Stripe, not a fake request.',
      },
      {
        name: 'stripe.signature_invalid',
        stage: 'Signature check ❌',
        data: 'reason',
        tooltip:
          'Rejected a webhook — its signature did not match, possible spoofing attempt.',
      },
      {
        name: 'stripe.event_received',
        stage: 'Event parse',
        data: 'event_type, event_id',
        tooltip:
          'Read the payment event from Stripe and identified what happened.',
      },
      {
        name: 'stripe.checkout_session_received',
        stage: 'checkout.session.completed',
        data: 'customer_id, subscription_id, has_client_reference_id',
        tooltip: 'A user just completed checkout and paid for a subscription.',
      },
      {
        name: 'stripe.subscription_price_fetched',
        stage: 'checkout: Stripe API',
        data: 'subscription_id, price_id',
        tooltip: 'Retrieved which plan the user subscribed to from Stripe.',
      },
      {
        name: 'stripe.tier_resolved / stripe.tier_resolve_failed',
        stage: 'checkout: Tier lookup',
        data: 'price_id, tier / customer_id',
        tooltip:
          'Matched the Stripe price to one of our plan tiers (Free, Core, or Family).',
      },
      {
        name: 'db.subscription_lookup',
        stage: 'checkout: DB read',
        data: 'customer_id, found, existing_tier',
        tooltip:
          'Checked whether this customer already has a subscription record.',
      },
      {
        name: 'db.subscription_updated / db.subscription_upserted',
        stage: 'checkout: DB write',
        data: 'sub_id / household_id, tier, is_upgrade',
        tooltip: "Updated the customer's subscription tier in our database.",
      },
      {
        name: 'stripe.subscription_update_received',
        stage: 'subscription.updated',
        data: 'customer_id, stripe_status, price_id, tier_resolved',
        tooltip:
          'A subscription changed — could be an upgrade, downgrade, or renewal.',
      },
      {
        name: 'db.existing_subscription_fetched',
        stage: 'subscription.updated: DB read',
        data: 'customer_id, existing_tier',
        tooltip:
          "Looked up the customer's current tier to compare with the new one.",
      },
      {
        name: 'db.subscription_updated',
        stage: 'subscription.updated: DB write',
        data: 'customer_id, new_status, tier, is_upgrade',
        tooltip: 'Saved the new subscription status and tier to our database.',
      },
      {
        name: 'stripe.subscription_deleted_received',
        stage: 'subscription.deleted',
        data: 'customer_id',
        tooltip: 'A subscription was cancelled in Stripe.',
      },
      {
        name: 'db.subscription_canceled',
        stage: 'subscription.deleted: DB write',
        data: 'customer_id',
        tooltip: "Marked the user's subscription as cancelled in our database.",
      },
    ],
  },
  {
    name: 'jobs-process-emails',
    priority: 'high',
    note: '9 checkpoints — queue + per-email LLM pipeline',
    breadcrumbs: [
      {
        name: 'queue.dequeued',
        stage: 'Queue read',
        data: 'source (pgmq|polling), email_count',
        tooltip: 'Picked up a batch of emails from the processing queue.',
      },
      {
        name: 'batch.started',
        stage: 'Batch start',
        data: 'email_count, llm_provider, llm_model, source',
        tooltip:
          'Starting to send all emails in this batch to the AI for extraction.',
      },
      {
        name: 'email.fetched',
        stage: 'Per-email: DB fetch',
        data: 'email_id, household_id, status, has_processed_content',
        tooltip: "Loaded a single email's content from the database.",
      },
      {
        name: 'email.skipped',
        stage: 'Per-email: skip gate',
        data: 'email_id, reason (subscription_paused | unexpected_status_*)',
        tooltip:
          "Skipped this email — the user's subscription is paused or the email is in an unexpected state.",
      },
      {
        name: 'llm.extraction_started',
        stage: 'Per-email: LLM start',
        data: 'email_id, extraction_id, provider, model, content_source',
        tooltip: 'Sent the email to the AI to extract events and tasks.',
      },
      {
        name: 'llm.extraction_failed',
        stage: 'Per-email: LLM fail ❌',
        data: 'email_id, extraction_id, reason, provider, latency_ms',
        tooltip: 'The AI could not extract events or tasks from this email.',
      },
      {
        name: 'email.processing_complete',
        stage: 'Per-email: done',
        data: 'email_id, extraction_id, provider, latency_ms, fallback_used',
        tooltip:
          'AI finished extracting events and tasks — ready for parent review.',
      },
      {
        name: 'email.processing_failed',
        stage: 'Per-email: fail ❌ (non-fatal)',
        data: 'email_id, reason',
        tooltip:
          'This email failed to process — marked as failed, other emails in the batch continue.',
      },
      {
        name: 'batch.completed',
        stage: 'Batch summary',
        data: 'total, processed, failed',
        tooltip: 'Finished processing all emails in the batch.',
      },
    ],
  },
  {
    name: 'jobs-renew-gmail-watch',
    priority: 'high',
    note: '6 checkpoints — per-subscription renewal loop',
    breadcrumbs: [
      {
        name: 'renewal.skipped',
        stage: 'Config check',
        data: 'reason (PUBSUB_TOPIC_NAME_not_configured)',
        tooltip:
          'Skipped renewal — the Google Pub/Sub topic is not configured yet.',
      },
      {
        name: 'subscriptions.fetched',
        stage: 'DB query',
        data: 'count, cutoff',
        tooltip:
          'Found Gmail connections that are about to expire and need renewal.',
      },
      {
        name: 'subscription.deactivated',
        stage: 'Per-sub: token fail ❌',
        data: 'gmail_address, revoked (bool)',
        tooltip:
          "Deactivated this Gmail connection — the user's access was revoked.",
      },
      {
        name: 'subscription.watch_renewal_failed',
        stage: 'Per-sub: Gmail API ❌',
        data: 'gmail_address, status',
        tooltip: 'Could not renew email listening for this Gmail account.',
      },
      {
        name: 'subscription.watch_renewed',
        stage: 'Per-sub: Gmail API ✓',
        data: 'gmail_address, new_expiry',
        tooltip: 'Successfully renewed email listening for this Gmail account.',
      },
      {
        name: 'renewal.completed',
        stage: 'Summary',
        data: 'renewed, failed, deactivated',
        tooltip: 'Finished renewing all Gmail connections due for refresh.',
      },
    ],
  },
  {
    name: 'emails-approve',
    priority: 'high',
    note: '9 checkpoints — auth → quota → RPC approve',
    breadcrumbs: [
      {
        name: 'email.loaded',
        stage: 'Email fetch',
        data: 'email_id, household_id, status',
        tooltip:
          "Loaded the email and confirmed it belongs to the user's household.",
      },
      {
        name: 'email.already_processed',
        stage: 'Idempotency exit',
        data: 'email_id',
        tooltip:
          'This email was already approved before — returning success immediately.',
      },
      {
        name: 'quota.checked',
        stage: 'Quota gate ✓',
        data: 'email_id, tier, used, cap',
        tooltip:
          'Verified the household has not hit their monthly approval limit.',
      },
      {
        name: 'quota.exceeded',
        stage: 'Quota gate ❌',
        data: 'email_id, tier, used, cap',
        tooltip:
          "Household has reached their plan's monthly limit — can't approve more this month.",
      },
      {
        name: 'review_items.loaded',
        stage: 'Review items fetch',
        data: 'email_id, items_count, events_count, tasks_count',
        tooltip: 'Loaded all extracted events and tasks from this email.',
      },
      {
        name: 'children.resolved',
        stage: 'Child name lookup',
        data: 'email_id, names_queried, names_resolved',
        tooltip:
          'Matched child names mentioned in the email to real household children.',
      },
      {
        name: 'rpc.approve_called',
        stage: 'RPC call',
        data: 'email_id, events_count, tasks_count',
        tooltip:
          'Calling the database function to create all events and tasks at once.',
      },
      {
        name: 'rpc.approve_failed',
        stage: 'RPC fail ❌',
        data: 'email_id, error',
        tooltip: 'Database commit failed — events and tasks were not created.',
      },
      {
        name: 'rpc.approve_succeeded',
        stage: 'RPC done',
        data: 'email_id, already_processed, events_created, tasks_created',
        tooltip: 'Events and tasks successfully added to the family schedule.',
      },
    ],
  },
];

const WEB_FLOWS: WebFlow[] = [
  {
    name: 'auth',
    file: 'context/AuthContext.tsx',
    note: '11 checkpoints — session restore, sign-in, sign-out',
    breadcrumbs: [
      {
        name: 'auth.session_restored',
        stage: 'Init: session check',
        data: 'has_session',
        tooltip: 'App just loaded — checking if the user is already logged in.',
      },
      {
        name: 'auth.profile_loaded',
        stage: 'Init: profile/role ✓',
        data: 'user_id',
        tooltip: "Loaded the user's profile and role from the database.",
      },
      {
        name: 'auth.profile_load_failed',
        stage: 'Init: profile/role ❌',
        data: '—',
        tooltip:
          "Could not load the user's profile — continuing with limited information.",
      },
      {
        name: 'auth.signed_in',
        stage: 'SIGNED_IN event',
        data: 'user_id',
        tooltip: 'User just signed in — starting to load their account data.',
      },
      {
        name: 'auth.provisioned',
        stage: 'Provision chain ✓',
        data: 'user_id',
        tooltip: "User's account is fully set up and ready to use the app.",
      },
      {
        name: 'auth.provision_failed',
        stage: 'Provision chain ❌',
        data: '—',
        tooltip:
          "Something went wrong setting up the user's account after sign-in.",
      },
      {
        name: 'auth.google_signin_started',
        stage: 'OAuth redirect',
        data: 'redirect_to',
        tooltip: 'User clicked "Sign in with Google" — redirecting to Google.',
      },
      {
        name: 'auth.google_signin_failed',
        stage: 'OAuth error ❌',
        data: '—',
        tooltip: 'Google sign-in failed before the redirect could happen.',
      },
      {
        name: 'auth.signout_started',
        stage: 'Sign-out begin',
        data: '—',
        tooltip: 'User clicked sign out — starting to clear their session.',
      },
      {
        name: 'auth.signout_completed',
        stage: 'Sign-out done ✓',
        data: '—',
        tooltip:
          'User successfully signed out and all session data was cleared.',
      },
      {
        name: 'auth.signout_failed',
        stage: 'Sign-out error ❌',
        data: '—',
        tooltip:
          'Sign-out ran into an error — session may not be fully cleared.',
      },
    ],
  },
  {
    name: 'email-approve',
    file: 'services/email.service.ts',
    note: '6 checkpoints — review items load + RPC approve',
    breadcrumbs: [
      {
        name: 'review_items.fetched',
        stage: 'Review items load',
        data: 'email_id, count',
        tooltip:
          'Loaded all the events and tasks from this email ready for review.',
      },
      {
        name: 'email.approve_started',
        stage: 'invokeFunction',
        data: 'email_id, has_child_id, has_item_member_ids',
        tooltip:
          'User clicked approve — sending the request to create events and tasks.',
      },
      {
        name: 'email.approve_quota_exceeded',
        stage: 'Quota gate ❌',
        data: 'tier, used, quota',
        tooltip:
          'Approval blocked — household has reached their monthly plan limit.',
      },
      {
        name: 'email.approve_failed',
        stage: 'HTTP / function error ❌',
        data: 'email_id',
        tooltip: 'Approval request failed due to a network or server error.',
      },
      {
        name: 'email.approve_items_failed',
        stage: 'Business logic fail ❌',
        data: 'email_id',
        tooltip:
          'Approval partially failed — some events or tasks could not be created.',
      },
      {
        name: 'email.approve_succeeded',
        stage: 'Approved ✓',
        data: 'email_id',
        tooltip:
          'All events and tasks were created and added to the family schedule.',
      },
    ],
  },
  {
    name: 'email-dismiss',
    file: 'services/email.service.ts',
    note: '3 checkpoints',
    breadcrumbs: [
      {
        name: 'email.dismiss_started',
        stage: 'invokeFunction',
        data: 'email_id',
        tooltip:
          'User clicked dismiss — sending the request to skip this email.',
      },
      {
        name: 'email.dismiss_failed',
        stage: 'Error ❌',
        data: 'email_id',
        tooltip:
          'Dismiss request failed — the email is still in the review queue.',
      },
      {
        name: 'email.dismiss_completed',
        stage: 'Dismissed ✓',
        data: 'email_id',
        tooltip: 'Email was dismissed and removed from the review queue.',
      },
    ],
  },
  {
    name: 'billing',
    file: 'services/billing.service.ts',
    note: '11 checkpoints — checkout, portal, plan changes',
    breadcrumbs: [
      {
        name: 'billing.checkout_started',
        stage: 'Checkout: invokeFunction',
        data: 'household_id, plan',
        tooltip:
          'User clicked upgrade — creating a Stripe checkout session for the selected plan.',
      },
      {
        name: 'billing.checkout_failed',
        stage: 'Checkout: error ❌',
        data: 'household_id, plan',
        tooltip: 'Could not create the Stripe checkout session.',
      },
      {
        name: 'billing.checkout_session_created',
        stage: 'Checkout: URL ✓',
        data: 'household_id, plan',
        tooltip:
          'Checkout session ready — redirecting user to the Stripe payment page.',
      },
      {
        name: 'billing.portal_started',
        stage: 'Portal: invokeFunction',
        data: 'household_id',
        tooltip:
          'Opening the Stripe customer portal to manage the subscription.',
      },
      {
        name: 'billing.portal_failed',
        stage: 'Portal: error ❌',
        data: 'household_id',
        tooltip: 'Could not open the Stripe customer portal.',
      },
      {
        name: 'billing.portal_session_created',
        stage: 'Portal: URL ✓',
        data: 'household_id',
        tooltip: 'Portal session ready — redirecting user to Stripe.',
      },
      {
        name: 'billing.plan_change_scheduled',
        stage: 'Schedule change: invokeFunction',
        data: 'household_id, target_tier, reason_key',
        tooltip:
          'User requested a plan downgrade — scheduling it for the end of the billing period.',
      },
      {
        name: 'billing.plan_change_failed',
        stage: 'Schedule change: error ❌',
        data: 'household_id, target_tier',
        tooltip: 'Could not schedule the plan change.',
      },
      {
        name: 'billing.plan_change_confirmed',
        stage: 'Schedule change: done ✓',
        data: 'household_id, target_tier, scheduled_change_at',
        tooltip:
          'Downgrade scheduled — will take effect at the end of the billing period.',
      },
      {
        name: 'billing.scheduled_change_cancelled',
        stage: 'Cancel change: invokeFunction',
        data: 'household_id',
        tooltip: 'User cancelled their pending plan downgrade.',
      },
      {
        name: 'billing.scheduled_change_cancel_failed',
        stage: 'Cancel change: error ❌',
        data: 'household_id',
        tooltip: 'Could not cancel the scheduled downgrade.',
      },
    ],
  },
];

function priorityVariant(p: 'critical' | 'high'): 'danger' | 'warning' {
  return p === 'critical' ? 'danger' : 'warning';
}

/**
 * How the edge verdict renders. Every branch is reachable: `initialized` is
 * the only green, and it is only ever produced by an edge isolate that
 * actually ran `Sentry.init`. `null` covers "we asked and could not find
 * out", which is a distinct answer from "it is fine".
 */
function edgeVerdict(
  edge: EdgeMonitoringHealth | null,
  fetchStatus: SentryHealthStatus,
): { label: string; badge: string; variant: BadgeVariant } {
  if (fetchStatus === 'idle' || fetchStatus === 'loading') {
    return { label: 'Checking...', badge: 'in flight', variant: 'info' };
  }
  if (!edge) {
    return { label: 'Unknown', badge: 'check failed', variant: 'danger' };
  }
  switch (edge.status) {
    case 'initialized':
      return {
        label: 'Reporting',
        badge: edge.environment ?? 'no environment',
        variant: 'success',
      };
    case 'misconfigured':
      return {
        label: 'Misconfigured',
        badge: 'needs attention',
        variant: 'warning',
      };
    case 'not_configured':
      return {
        label: 'Not configured',
        badge: 'SENTRY_DSN unset',
        variant: 'danger',
      };
  }
}

export interface AdminSentryProps {
  /** Admin password, held by AdminShell and never persisted. */
  password: string;
}

export function AdminSentry({ password }: AdminSentryProps) {
  const webConfigured = !!import.meta.env.VITE_SENTRY_DSN;
  const {
    status: healthStatus,
    data: health,
    error: healthError,
    refetch: refetchHealth,
  } = useAdminSentryHealth(password);
  const edge = health?.edge ?? null;
  const verdict = edgeVerdict(edge, healthStatus);
  const [selectedFn, setSelectedFn] = useState(WIRED_FUNCTIONS[0].name);
  const activeFn =
    WIRED_FUNCTIONS.find((f) => f.name === selectedFn) ?? WIRED_FUNCTIONS[0];
  const [selectedFlow, setSelectedFlow] = useState(WEB_FLOWS[0].name);
  const activeFlow =
    WEB_FLOWS.find((f) => f.name === selectedFlow) ?? WEB_FLOWS[0];

  return (
    <div className="flex flex-col gap-6">
      {/* Status cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* A REAL check (YAR-373).
            This card once rendered a hardcoded green "Active — N / N
            functions", where the badge compared an array's length against
            itself: it read 100% healthy in an environment with no DSN at
            all. PR #327 removed the false green but could only say the page
            was unable to look, because `SENTRY_DSN` lives in the Supabase
            vault and the browser cannot read it.
            It now asks something that can. `admin-sentry-health` reports the
            outcome of the same `ensureMonitoring()` call every edge function
            makes at boot, so this card shows a decision that was actually
            acted on rather than one re-derived here — and it can go red,
            amber, or unknown. Presence and environment only; the DSN value
            never crosses to the browser. */}
        <Card className="p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              Edge monitoring
            </span>
            <button
              type="button"
              onClick={refetchHealth}
              className="text-xs text-muted-foreground underline hover:text-foreground disabled:opacity-50"
              disabled={healthStatus === 'loading'}
            >
              Re-check
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xl font-semibold">{verdict.label}</span>
            <Badge
              variant={verdict.variant}
              size="sm"
            >
              {verdict.badge}
            </Badge>
          </div>
          {healthStatus === 'error' && (
            <p className="text-xs text-danger">
              Could not reach admin-sentry-health: {healthError}
            </p>
          )}
          {/* `problems` is non-empty exactly when the status is not
              `initialized`, so this never renders an empty failure state. */}
          {edge && edge.problems.length > 0 && (
            <ul className="text-xs text-muted-foreground flex flex-col gap-1">
              {edge.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {edge?.status === 'initialized' && (
            <p className="text-xs text-muted-foreground">
              {WIRED_FUNCTIONS.length} functions wired · traces{' '}
              {edge.tracesSampleRate ?? '—'}
              {edge.debug ? ' · debug on' : ''}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            &quot;Reporting&quot; means the SDK initialized against a valid
            config — delivery to Sentry is not observable from here.
          </p>
        </Card>
        <Card className="p-4 flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">
            Web app monitoring
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">
              {webConfigured ? 'Active' : 'Not configured'}
            </span>
            <Badge
              variant={webConfigured ? 'success' : 'warning'}
              size="sm"
            >
              {webConfigured
                ? 'VITE_SENTRY_DSN set'
                : 'VITE_SENTRY_DSN not set'}
            </Badge>
          </div>
        </Card>
        <Card className="p-4 flex flex-col gap-2">
          {/* Which SDK is installed — a fact about the build, not a claim that
              it is reporting. "Active" here was the same unverifiable green as
              the card above. */}
          <div className="text-sm text-muted-foreground">Edge SDK</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">@sentry/deno</span>
            <Badge
              variant="info"
              size="sm"
            >
              installed
            </Badge>
          </div>
        </Card>
        <Card className="p-4 flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">Flush strategy</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">Before rethrow</span>
            <Badge
              variant="info"
              size="sm"
            >
              2 s timeout
            </Badge>
          </div>
        </Card>
      </section>

      {/* Wired functions */}
      <section>
        <Card className="p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Wired functions</h2>
            {/* Was a green "N active". Nothing here checks that they are
                active — it is the length of a hand-maintained list of
                functions with instrumentation in their source. Whether that
                instrumentation reports is the Edge monitoring card's
                question, and this badge should not appear to answer it. */}
            <Badge
              variant="info"
              size="sm"
            >
              {WIRED_FUNCTIONS.length} wired in code
            </Badge>
          </div>
          <div className="flex flex-col gap-2">
            {WIRED_FUNCTIONS.map((fn) => (
              <div
                key={fn.name}
                className="flex items-start justify-between gap-4 text-sm"
              >
                <span className="font-mono text-foreground">{fn.name}</span>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-muted-foreground text-right">
                    {fn.note}
                  </span>
                  <Badge
                    variant={priorityVariant(fn.priority)}
                    size="sm"
                  >
                    {fn.priority}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Breadcrumb trail — tabbed per function */}
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold">Breadcrumb trails</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Breadcrumbs accumulate silently on every request and are only sent
            to Sentry when an unhandled error is thrown. Select a function to
            see its full checkpoint trail.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1">
          {WIRED_FUNCTIONS.map((fn) => (
            <button
              key={fn.name}
              type="button"
              onClick={() => setSelectedFn(fn.name)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-colors cursor-pointer ${
                selectedFn === fn.name
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {fn.name}
            </button>
          ))}
        </div>

        {/* Active function breadcrumb table */}
        <Card
          variant="default"
          padding="none"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-mono text-sm font-medium">
              {activeFn.name}
            </span>
            <Badge
              variant="info"
              size="sm"
            >
              {activeFn.breadcrumbs.length} checkpoints
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-3 font-medium w-8">#</th>
                  <th className="p-3 font-medium">Breadcrumb</th>
                  <th className="p-3 font-medium">Stage</th>
                  <th className="p-3 font-medium">Data captured</th>
                </tr>
              </thead>
              <tbody>
                {activeFn.breadcrumbs.map((b, i) => (
                  <tr
                    key={b.name}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="p-3 text-muted-foreground tabular-nums">
                      {i + 1}
                    </td>
                    <td className="p-3 font-mono text-xs text-foreground">
                      <span
                        title={b.tooltip}
                        className="underline decoration-dotted underline-offset-2 decoration-muted-foreground cursor-help"
                      >
                        {b.name}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{b.stage}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">
                      {b.data}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* Web breadcrumb trails */}
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold">Web breadcrumb trails</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manual breadcrumbs on critical web flows. Accumulated silently and
            sent to Sentry only when an unhandled error reaches the
            ErrorBoundary or window.onerror.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1">
          {WEB_FLOWS.map((flow) => (
            <button
              key={flow.name}
              type="button"
              onClick={() => setSelectedFlow(flow.name)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-colors cursor-pointer ${
                selectedFlow === flow.name
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {flow.name}
            </button>
          ))}
        </div>

        {/* Active flow breadcrumb table */}
        <Card
          variant="default"
          padding="none"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm font-medium">
                {activeFlow.name}
              </span>
              <span className="text-xs text-muted-foreground font-mono">
                {activeFlow.file}
              </span>
            </div>
            <Badge
              variant="info"
              size="sm"
            >
              {activeFlow.breadcrumbs.length} checkpoints
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-3 font-medium w-8">#</th>
                  <th className="p-3 font-medium">Breadcrumb</th>
                  <th className="p-3 font-medium">Stage</th>
                  <th className="p-3 font-medium">Data captured</th>
                </tr>
              </thead>
              <tbody>
                {activeFlow.breadcrumbs.map((b, i) => (
                  <tr
                    key={b.name}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="p-3 text-muted-foreground tabular-nums">
                      {i + 1}
                    </td>
                    <td className="p-3 font-mono text-xs text-foreground">
                      <span
                        title={b.tooltip}
                        className="underline decoration-dotted underline-offset-2 decoration-muted-foreground cursor-help"
                      >
                        {b.name}
                      </span>
                    </td>
                    <td className="p-3 text-muted-foreground">{b.stage}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">
                      {b.data}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* How it works */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Edge — how it works</h2>
            <Badge
              variant="info"
              size="sm"
            >
              Deno
            </Badge>
          </div>
          <dl className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Initialization</dt>
              <dd className="font-mono text-right text-xs">
                ensureMonitoring() before Deno.serve()
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Handler wrapper</dt>
              <dd className="font-mono text-right text-xs">
                createEdgeFunctionWrapper()
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Error capture</dt>
              <dd className="text-right">
                catch → captureException → flush(2s) → rethrow
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Breadcrumbs</dt>
              <dd className="text-right">
                addBreadcrumb() at each checkpoint, sent only on error
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Shared helper</dt>
              <dd className="font-mono text-right text-xs">
                _shared/monitoring.ts
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">DSN source</dt>
              <dd className="font-mono text-right text-xs">
                SENTRY_DSN (Supabase vault secret)
              </dd>
            </div>
          </dl>
        </Card>

        <Card className="p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Web — how it works</h2>
            <Badge
              variant="info"
              size="sm"
            >
              React
            </Badge>
          </div>
          <dl className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Initialization</dt>
              <dd className="font-mono text-right text-xs">
                initSentry() in main.tsx before createRoot()
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Render errors</dt>
              <dd className="font-mono text-right text-xs">
                {'<ErrorBoundary>'} wraps App.tsx root
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Unhandled errors</dt>
              <dd className="text-right">
                SDK auto-captures window.onerror + unhandledrejection
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">User context</dt>
              <dd className="text-right">
                setUserContext() on sign-in, cleared on sign-out
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Source maps</dt>
              <dd className="text-right">
                sentryVitePlugin at build time (CI only)
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">DSN source</dt>
              <dd className="font-mono text-right text-xs">
                VITE_SENTRY_DSN (Railway env var)
              </dd>
            </div>
          </dl>
        </Card>
      </section>

      {/* Sentry dashboard link */}
      <section className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => window.open('https://sentry.io', '_blank')}
          rightIcon={<ExternalLink size={14} />}
        >
          Open Sentry
        </Button>
      </section>
    </div>
  );
}
